<template>
    <section class="container-fluid content-type-37 actions">
        <div class="container">
            <div class="row">
                <div class="col-12 col-md-10 col-lg-8">
                    <div class="content">
                        <div class="buttons">
                            <button class="btn-2 btn-m-icon" @click="routeToMode()">
                                <div class="icon-company-neg"></div>
                                <span>{{$t('links.companies')}}</span>
                            </button>
                            <button class="btn-7 btn-m-icon" :class="{'btn-paywall': paywall}" :disabled="paywall || !suspectsAvailable" @click="routeToMode('new')">
                                <div v-if="paywall" class="paywall-lock"></div>
                                <div class="icon-new-pos"></div>
                                <span>{{$t('links.suspect')}}</span>
                            </button>
                            <button class="btn-1 btn-m-icon" :disabled="!audience?.stats?.prospects > 0" @click="routeToMode('prospects')">
                                <div class="icon-target-pos"></div>
                                <span>{{$t('links.prospects')}}</span>
                            </button>
                        </div>

                        <div class="buttons">
                            <button class="btn-6 btn-s-icon" @click="fetchStats()">
                                <div class="icon-reset-pos"></div>
                                <span>{{$t(audience?.stats ? 'views.audience.buttons.recalculate' : 'views.audience.buttons.recalculate_3')}}</span>
                            </button>
                            <button class="btn-1 btn-s-icon" :class="{'btn-paywall': paywall || noSubscription}" v-if="suspectsAvailable" :disabled="!bulkAvailable || noSubscription" @click="bulkAddPopup()">
                                <div v-if="paywall || noSubscription" class="paywall-lock"></div>
                                <div class="icon-plus-pos"></div>
                                <span>{{$t('views.audience.buttons.bulk_add')}}</span>
                            </button>
                            <button class="btn-2 btn-s-icon" :disabled="!(audience?.stats?.prospects > 0)" @click="confirmDownloadPopup()">
                                <div class="icon-download-neg"></div>
                                <span>{{$t('views.audience.buttons.download')}}</span>
                            </button>
                        </div>
                        <div class="p notice" v-if="audience?.stats?.stats_exact">{{$t(audience.stats.stats_exact ? 'views.audience.notices.exact' : 'views.audience.notices.estimation')}}</div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
    import { markRaw } from 'vue';
    import i18n from '@/plugins/i18n';
    import { mapActions, mapState } from 'pinia';
    import { useAudienceStore } from '@/stores/audience';
    import { useAccountStore } from '@/stores/account';
    import { usePopupStore } from '@/stores/utils/popup';
    import { useCompaniesStore } from '@/stores/companies';
    import { useToastStore } from '@/stores/utils/toast';
    import { useApiCreditsStore } from '@/stores/apiCredits';
    import AudienceBulkAddForm from '@/components/forms/AudienceBulkAddForm';
    import { formatNumber } from '@/utils/formatting';

    export default {
        name: "AudienceActions",
        computed: {
            ...mapState(useAudienceStore, ['audience']),
            ...mapState(useAccountStore, ['account', 'paywall', 'productType']),
            ...mapState(useCompaniesStore, ['downloadLinks']),
            ...mapState(useApiCreditsStore, ['ppCredits']),
            bulkAvailable() {
                return this.creditsAvailable > 0 && this.suspectsAvailable > 0;
            },
            creditsAvailable() {
                if (this.ppCredits) {
                    const count = this.ppCredits.monthly_credits - this.ppCredits.credits_used_month;
                    return count > 0 ? count : 0;
                }
                return 0;
            },
            suspectsAvailable() {
                return (this.audience?.total || 0) - (this.audience?.stats?.prospects || 0);
            },
            noSubscription() {
                return !this.productType || this.productType === process.env.VUE_APP_PRODUCT_FREE || this.productType === process.env.VUE_APP_PRODUCT_TRIAL;
            },
        },
        methods: {
            formatNumber,
            ...mapActions(useAudienceStore, ['fetchStats']),
            ...mapActions(useCompaniesStore, ['downloadCompanies']),
            ...mapActions(useApiCreditsStore, ['fetchApiCredits']),
            routeToMode(mode = 'all') {
                switch (mode) {
                    case 'new':
                        this.$router.push({ name: 'audienceSuspects', params: { id: this.audience.audience_id } });
                        break;
                    case 'prospects':
                        this.$router.push({ name: 'audienceProspects', params: { id: this.audience.audience_id } });
                        break;
                    default:
                        this.$router.push({ name: 'audienceCompanies', params: { id: this.audience.audience_id } });
                        break;
                }
            },
            async bulkAddPopup() {
                await Promise.all([
                    this.fetchApiCredits(),
                    this.fetchStats()
                ]);

                // Prevent timing issues.
                const ppCredits = useApiCreditsStore().ppCredits;
                const creditsAvailable = ppCredits ? 
                    Math.max(0, ppCredits.monthly_credits - ppCredits.credits_used_month) : 0;

                if(!creditsAvailable) {
                    this.noCreditsPopup();
                    return;
                }

                // Prevent timing issues.
                const audience = useAudienceStore().audience;
                const suspectsAvailable = audience ? 
                    Math.max(0, (audience.total || 0) - (audience.stats?.prospects || 0)) : 0;

                if(!suspectsAvailable) {
                    this.noNewProspectsPopup()
                    return;
                }

                usePopupStore().showPopup('form', {
                    title: 'popups.audience_bulk_start.title',
                    description: 'popups.audience_bulk_start.description',
                    formComponent: markRaw(AudienceBulkAddForm),
                    formProps: {
                        credits: creditsAvailable,
                        suspects: suspectsAvailable
                    },
                    onFormSuccess: async () => {
                        await this.fetchStats();
                        useToastStore().addToast(i18n.global.t('toasts.success.audience_bulk_added'), 'success');
                    }
                });
            },
            noNewProspectsPopup() {
                usePopupStore().showPopup('notice', {
                    title: 'popups.audience_no_prospects.title',
                    description: 'popups.audience_no_prospects.description',
                    closeButtonText: 'common.close'
                });
            },
            noCreditsPopup() {
                usePopupStore().showPopup('notice', {
                    title: 'popups.audience_no_credits.title',
                    description: 'popups.audience_no_credits.description',
                    closeButtonText: 'common.close'
                });
            },
            async fetchDownloadUrls() {
                const downloadReady = await this.downloadCompanies(true, this.audience.qid || null);
                if(downloadReady) {
                    return true;
                }
                return false;
            },
            startDownload() {
                if(this.downloadLinks && Object.keys(this.downloadLinks).length > 0) {
                    const url = this.downloadLinks.export_url_zip || this.downloadLinks.export_url_csv || this.downloadLinks.export_url_xlsx;
                    window.open(url, "_blank"); 
                }
            },
            async confirmDownloadPopup() {
                await this.fetchStats();

                if(!this.audience?.stats?.prospects || this.audience.stats.prospects < 1) {
                    return;
                }

                usePopupStore().showPopup('fetch', {
                    title: 'popups.confirm_download_audience.title',
                    description: 'popups.confirm_download_audience.description',
                    btnConfirm: "popups.confirm_download_audience.button_confirm",
                    btnCancel: "common.cancel_1",
                    fetchFunction: this.fetchDownloadUrls,
                    onFetchSuccess: this.startDownloadPopup,
                    translationPlaceholders: {
                        description: {
                            name: this.audience.name,
                            count: this.audience.stats.prospects,
                        }
                    }
                });
            },
            startDownloadPopup() {
                usePopupStore().showPopup('confirm', {
                    title: 'popups.start_download.title',
                    description: 'popups.start_download.description',
                    btnConfirm: "popups.start_download.button_confirm",
                    btnCancel: "common.cancel_1",
                    confirmFunction: this.startDownload,
                });
            }
        },
    }
</script>